// src/auth/apiClient.ts
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import { tokenStore, Keys } from "./tokenStore.ts";

/* ================== Базовые URL ================== */
const isDev = process.env.NODE_ENV !== "production";

// Домен jetrub.cc просрочен 02.11.2026 — регистратор увёл NS на заглушку, и API стало
// недоступно. Переехали на поддомен zont1k в stradateam.com (Cloudflare, до апреля 2027).
export const AUTH_BASE =
  (isDev ? process.env.REACT_APP_AUTH_API_DEV : process.env.REACT_APP_AUTH_API) ||
  "https://api.zont1k.stradateam.com";

export const OPS_BASE =
  (isDev ? process.env.REACT_APP_OPS_API_DEV : process.env.REACT_APP_OPS_API) ||
  "https://os.zont1k.stradateam.com";

/* ================== Клиенты ================== */
export const authHttp: AxiosInstance = axios.create({ baseURL: AUTH_BASE });
export const opsHttp: AxiosInstance = axios.create({ baseURL: OPS_BASE });

// "Голый" клиент без интерцепторов и без Authorization — ТОЛЬКО для refresh
const bareAuth: AxiosInstance = axios.create({ baseURL: AUTH_BASE });

/* ================== Helpers ================== */
function setAuthHeader<T extends { headers?: any }>(
  config: T,
  token?: string | null
): T {
  if (!token) return config;

  // Axios v1: headers может быть экземпляром AxiosHeaders
  if (!config.headers) {
    // создаём заголовки, где это уместно
    config.headers = new AxiosHeaders() as any;
  }
  const hdrs = config.headers;

  if (typeof hdrs.set === "function") {
    hdrs.set("Authorization", `Bearer ${token}`);
  } else {
    // plain object
    hdrs.Authorization = `Bearer ${token}`;
  }
  return config;
}

/* ================== Подстановка Bearer ================== */
const withAuthHeader = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const access = tokenStore.get(Keys.Access);
  return setAuthHeader(config, access);
};

authHttp.interceptors.request.use(withAuthHeader);

/* ===== Проактивный refresh перед каждым запросом в OPS, если токен скоро истечёт ===== */
opsHttp.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const exp = +(tokenStore.get(Keys.ExpAt) || 0);
  const now = Math.floor(Date.now() / 1000);
  if (exp && exp - 30 <= now) {
    try {
      const newAccess = await refreshAccessToken(); // см. ниже
      return setAuthHeader(config, newAccess);
    } catch {
      // если рефреш не удался — пусть обработается в response interceptor
    }
  }
  return withAuthHeader(config);
});

/* ================== refresh flow ================== */
let refreshing: Promise<string> | null = null;

type RefreshResponse = {
  accessToken: string;
  refreshToken: string;
  expiresAt: string | number | Date; // ISO строка или число
  role?: string | number | null;
  userId?: string | number | null;
  officeId?: string | number | null;
  username?: string | null;
};

async function refreshAccessToken(): Promise<string> {
  if (!refreshing) {
    const refreshToken = tokenStore.get(Keys.Refresh);
    if (!refreshToken) throw new Error("No refresh token");

    // ВАЖНО: используем bareAuth (без Authorization)
    refreshing = bareAuth
      .post<RefreshResponse>("/api/auth/refresh", { refreshToken })
      .then(({ data }) => {
        const {
          accessToken,
          refreshToken: newRefresh,
          expiresAt,
          role,
          userId,
          officeId,
          username,
        } = data || ({} as RefreshResponse);
        if (!accessToken || !newRefresh) throw new Error("Bad refresh payload");

        tokenStore.set(Keys.Access, accessToken);
        tokenStore.set(Keys.Refresh, newRefresh);

        const expSec =
          typeof expiresAt === "number"
            ? Math.floor(+expiresAt / 1000)
            : Math.floor(new Date(expiresAt).getTime() / 1000);

        tokenStore.set(Keys.ExpAt, String(expSec));
        if (role != null) tokenStore.set(Keys.Role, String(role));
        if (userId != null) tokenStore.set(Keys.User, String(userId));
        if (officeId != null) tokenStore.set(Keys.Office, String(officeId));
        if (username) tokenStore.set(Keys.Name, username);

        // Обновим дефолтные заголовки (через индексатор, чтобы не ругался TS)
        (authHttp.defaults.headers.common as any)["Authorization"] = `Bearer ${accessToken}`;
        (opsHttp.defaults.headers.common as any)["Authorization"] = `Bearer ${accessToken}`;

        return accessToken;
      })
      .catch((e) => {
        // refresh провалился — чистим токены
        tokenStore.clearAll();
        throw e;
      })
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing!; // гарантируем не-null
}

const isAuthUrl = (url = ""): boolean => url.includes("/api/auth/");

interface RetriableConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const onResponseError = async (error: AxiosError, client: AxiosInstance) => {
  const status = error?.response?.status;
  const original = (error?.config || {}) as RetriableConfig;

  if (status === 401) {
    // не зацикливаемся и не рефрешим auth-эндпоинты
    if (original._retry || isAuthUrl(original.url || "")) {
      tokenStore.clearAll();
      return Promise.reject(error);
    }

    try {
      const newAccess = await refreshAccessToken();
      original._retry = true;

      if (!original.headers) original.headers = {};
      // На конфиг ретрая достаточно plain object:
      (original.headers as any).Authorization = `Bearer ${newAccess}`;

      return client(original); // повторяем исходный запрос
    } catch (e) {
      tokenStore.clearAll();
      return Promise.reject(e);
    }
  }

  return Promise.reject(error);
};

/* ================== account: changePassword ================== */
export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}
export interface ChangePasswordOptions {
  signal?: AbortSignal;
}
export type ChangePasswordResult =
  | { ok: true; status: number }
  | { ok: false; status: number; message: string };

export async function changePassword(
  payload: ChangePasswordPayload,
  opts: ChangePasswordOptions = {}
): Promise<ChangePasswordResult> {
  const { currentPassword, newPassword } = payload || ({} as ChangePasswordPayload);
  if (!currentPassword || !newPassword) {
    return { ok: false, status: 0, message: "currentPassword и newPassword обязательны" };
  }

  try {
    // validateStatus пропускает 4xx в then, чтобы достать текст ошибки без catch
    const res = await authHttp.post(
      "/api/account/change-password",
      { currentPassword, newPassword },
      {
        signal: opts.signal,
        validateStatus: (s: number) => s >= 200 && s < 500,
      }
    );

    if (res.status === 200 || res.status === 204) {
      return { ok: true, status: res.status };
    }

    const data = res?.data as unknown;
    const msg =
      ((typeof data === "string" && data) as string) || res?.statusText || "Не удалось обновить пароль";

    return { ok: false, status: res.status, message: msg };
  } catch (e: any) {
    // сеть/прерывание/неожиданная ошибка
    const msg = (e?.message as string) || "Сеть недоступна";
    return { ok: false, status: 0, message: msg };
  }
}

export const accountApi = { changePassword };

/* ================== Response interceptors ================== */
authHttp.interceptors.response.use(
  (r) => r,
  (e: AxiosError) => onResponseError(e, authHttp)
);
opsHttp.interceptors.response.use(
  (r) => r,
  (e: AxiosError) => onResponseError(e, opsHttp)
);

/* Для совместимости */
export const http = authHttp;
